import type { JSX } from 'react';

import type { ToolUseItem } from '../../../../contracts/harness.types';
import { friendlyToolError } from '../../../../data/projects/components/projectChatMessage.helpers';
import {
  getWorkHostPort,
  type WorkHostBridgePorts,
} from '../../../work-home/work-home-ports';

import { readString, readToolData, readToolError } from './shared/readers';
import { toCreateTemplateStatus } from './status';

type CreateTemplateBlockProps = Parameters<WorkHostBridgePorts['CreateTemplateBlock']>[0];

// eslint-disable-next-line react-refresh/only-export-components -- Adapter exports must keep their existing render function names.
const CreateTemplateToolBlock = (
  props: Omit<CreateTemplateBlockProps, 'onBlockError'>
): JSX.Element => {
  const bridge = getWorkHostPort('bridge');
  const reportBlockError = bridge.useBlockErrorReporter();
  const CreateTemplateBlock = bridge.CreateTemplateBlock;
  return <CreateTemplateBlock {...props} onBlockError={reportBlockError} />;
};

export const renderCreateTemplateToolUse = (item: ToolUseItem): JSX.Element | null => {
  // A queued create can still be the first event of a multi-template approval
  // batch. Let the timeline's quiet Working state own this ambiguous phase;
  // rendering a singular card here causes a visible one-template → batch swap.
  if (item.status === 'queued') return null;
  const status = toCreateTemplateStatus(item.status);
  if (!status) return null;

  const data = readToolData(item);
  const templateId =
    readString(data, 'template_id') ?? readString(item.input, 'template_id');
  // create_template's result names the template field `name` (not template_name).
  const templateName = readString(data, 'name') ?? readString(item.input, 'name');

  // Without anything to identify the template the rich block carries no more
  // information than GenericToolUseRow — fall through cleanly.
  if (!templateId && !templateName) return null;

  if (status === 'error') {
    return (
      <CreateTemplateToolBlock
        status="error"
        templateId={templateId}
        templateName={templateName}
        error={friendlyToolError(readToolError(item))}
      />
    );
  }

  return (
    <CreateTemplateToolBlock
      status={status}
      templateId={templateId}
      templateName={templateName}
    />
  );
};
